/**
 * Scan POS P6 -- the ONE product-lookup + Cart-add path of the Sale screen.
 *
 *   Physical scanner / keyboard ─┐
 *   Manual code input ───────────┼──► resolveProductByCode ──► addProductToSale ──► computeTotals
 *   Remote SCAN_EVENT ───────────┘
 *
 * Extracted verbatim from `components/sale/index.tsx` (onSearchInputChange,
 * onManageProduct('A'), updateProduct, calculate) so a remote scan and a
 * typed code produce the same row, price and totals. Pure functions: the
 * Sale screen holds the state, these compute the next value. A Remote scan
 * adds exactly REMOTE_SCAN_QUANTITY (1); it never reads the manual
 * quantity field.
 */
import { api_product_lookup_code } from '../api/product';
import { isEmpty } from '../utils/util';

export const REMOTE_SCAN_QUANTITY = 1;

export type LookupSource = 'LOCAL' | 'API';

export type LookupResult =
    | { status: 'FOUND'; product: any; source?: LookupSource }
    | { status: 'NOT_FOUND' }
    | { status: 'UNAVAILABLE' };

export type ProductLookup = (code: string) => Promise<any>;

const findLocal = (productList: any[], code: string) =>
    (productList || []).filter((x: any) => x['product_code'] == code);

/**
 * The product for [code]: the API first, then the screen's already-loaded
 * product list -- exactly the rules the barcode box always had.
 *   API answered with a product           -> FOUND (API row)
 *   API answered "none" + local has it    -> FOUND (local row)
 *   API answered "none" + local does not  -> NOT_FOUND (proven: no product)
 *   API FAILED + local has it             -> FOUND (local row)
 *   API FAILED + local does not           -> UNAVAILABLE (nothing proven)
 * The code is matched as text: never cast, so leading zeroes stay.
 */
export const resolveProductByCode = async (
    code: string,
    productList: any[],
    lookup: ProductLookup = api_product_lookup_code,
): Promise<LookupResult> => {
    try {
        const target = await lookup(code);
        if (target && (target.id || target.product_code)) {
            return { status: 'FOUND', product: target };
        }
        const local = findLocal(productList, code);
        return local.length > 0 ? { status: 'FOUND', product: local[0] } : { status: 'NOT_FOUND' };
    } catch {
        const local = findLocal(productList, code);
        return local.length > 0 ? { status: 'FOUND', product: local[0] } : { status: 'UNAVAILABLE' };
    }
};

/** Exact text match, never `==`: '000123' must not match 123. */
const findLocalExact = (productList: any[], code: string) =>
    (productList || []).filter((x: any) =>
        x && x['product_code'] !== undefined && x['product_code'] !== null && String(x['product_code']) === code);

/**
 * Latency R1 -- the Remote Scan lookup ONLY (typed / physical-scanner codes
 * keep `resolveProductByCode` above, unchanged).
 *
 *   the Sale screen's already-loaded product list has it   -> FOUND (LOCAL), no network
 *   otherwise the Product API, exactly as before:
 *     API answered with a product                          -> FOUND (API)
 *     API answered "none"                                  -> NOT_FOUND
 *     API FAILED                                           -> UNAVAILABLE
 *
 * The list is LIST page 1 (newest 200 products), loaded when the Sale screen
 * opens; a product outside it still goes to the API. Its rows carry the
 * same columns GET_BY_CONDITION returns (prices, type, unit) and the same
 * active / not-deleted filter. A code shared by two different products is
 * not guessed between locally -- the API decides, as it always did.
 *
 * Freshness: a price or product changed AFTER the screen loaded is not seen
 * by a local hit until the screen reloads its list. Accepted for R1; a
 * refresh policy is a separate decision.
 */
export const resolveRemoteProductByCode = async (
    code: string,
    productList: any[],
    lookup: ProductLookup = api_product_lookup_code,
): Promise<LookupResult> => {
    const local = findLocalExact(productList, code);
    if (local.length > 0 && local.every((x: any) => x['id'] === local[0]['id'])) {
        return { status: 'FOUND', product: local[0], source: 'LOCAL' };
    }
    try {
        const target = await lookup(code);
        if (target && (target.id || target.product_code)) {
            return { status: 'FOUND', product: target, source: 'API' };
        }
        return { status: 'NOT_FOUND' };
    } catch {
        return { status: 'UNAVAILABLE' };
    }
};

/** The manual quantity rule: an empty / zero / negative box means 1. */
export const manualQuantity = (productQty: any): number =>
    (isEmpty(productQty) || productQty <= 0 ? 1 : productQty);

/** Every row priced by the sale's own type, total = price x qty - discount. */
export const repriceSale = (object: any): any => {
    const detail: any[] = [];
    (object.detail || []).forEach((x: any) => {
        const price = object['sale_type'] == 1 ? x['price_retail'] : x['price_wholesale'];
        const qty = isEmpty(x['quantity']) ? 0 : x['quantity'];
        const discount = isEmpty(x['discount']) ? 0 : x['discount'];
        x['price'] = price;
        x['total_price'] = (price * qty) - discount;
        detail.push(x);
    });
    object.detail = detail;
    return object;
};

/**
 * The next sale after adding [quantity] of [product]. Never mutates
 * [sale]: the caller commits the returned object or nothing at all.
 * An existing row (same product id) grows; otherwise a new row is added,
 * priced by [saleTypeForNewRow] and then repriced by the sale's type --
 * the two steps the screen always did.
 */
export const addProductToSale = (sale: any, product: any, quantity: number, saleTypeForNewRow: number): any => {
    if (!product || (product.id === undefined && product.product_code === undefined)) {
        throw new Error('no product to add');
    }
    const object: any = JSON.parse(JSON.stringify(sale));
    object.detail = object.detail || [];
    const index = object.detail.findIndex((x: any) => x['product'] == product['id']);
    if (index >= 0) {
        const qty = object.detail[index]['quantity'];
        object.detail[index]['quantity'] = parseInt(isEmpty(qty) ? 0 : qty) + quantity;
    } else {
        const price = saleTypeForNewRow == 1 ? product['price_retail'] : product['price_wholesale'];
        object.detail = [
            ...object.detail,
            {
                key: object.detail.length + 1,
                id: 0,
                withdraw: 0,
                quantity,
                weight: 0,
                price,
                total_price: price,
                discount: 0,
                point: 0,
                status: 'Y',
                product: product['id'],
                product_code: product['product_code'],
                product_name: product['product_name'],
                product_name_other: product['product_name_other'],
                product_type: product['product_type'],
                product_unit: product['product_unit'],
                price_retail: product['price_retail'],
                price_wholesale: product['price_wholesale'],
            },
        ];
    }
    return repriceSale(object);
};

export interface SaleTotals {
    price: number;
    discount: number;
    discountBill: number;
    vatAmount: number;
    totalPrice: number;
}

/** The footer figures, exactly as `calculate` always computed them. */
export const computeTotals = (object: any, vatFallback: number): SaleTotals => {
    let itemsGross = 0;
    let discount = 0;
    let vatAmt = 0;
    let total = 0;
    const discountBillVal = parseFloat(object.discount_bill) || 0;

    (object.detail || []).forEach((x: any) => {
        const itemPrice = parseFloat(x['price']) || 0;
        const itemQty = parseFloat(x['quantity']) || 0;
        const itemDiscount = parseFloat(x['discount']) || 0;
        itemsGross += itemPrice * itemQty;
        discount += itemDiscount;
    });

    const itemsNet = Math.max(0, itemsGross - discount);
    const netAfterBillDiscount = Math.max(0, itemsNet - discountBillVal);

    const vatType = parseInt(object.vat !== undefined ? object.vat : vatFallback);
    if (vatType === 0) {
        // Vat out (ภาษีแยกนอก 7%)
        vatAmt = parseFloat((netAfterBillDiscount * 0.07).toFixed(2));
        total = parseFloat((netAfterBillDiscount + vatAmt).toFixed(2));
    } else if (vatType === 1) {
        // Vat in (ภาษีรวมใน 7%)
        vatAmt = parseFloat(((netAfterBillDiscount * 7) / 107).toFixed(2));
        total = parseFloat(netAfterBillDiscount.toFixed(2));
    } else {
        vatAmt = 0;
        total = parseFloat(netAfterBillDiscount.toFixed(2));
    }

    return {
        price: parseFloat(itemsGross.toFixed(2)),
        discount: parseFloat(discount.toFixed(2)),
        discountBill: discountBillVal,
        vatAmount: vatAmt,
        totalPrice: total,
    };
};
